"use client";

import { Button, HStack, Icon, Text, VStack } from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import type { CreatePaymentPayload } from "@/_services/postRequestForm";
import { InputField } from "@/_components/ui/form/InputField";
import { SimpleSelectField } from "@/_components/ui/form/SimpleSelect";
import { TextareaField } from "@/_components/ui/form/TextareaField";
import { useTranslations } from "next-intl";
import { IoSend } from "react-icons/io5";

type PaymentRequestFormProps = {
  onSuccess?: () => void;
};

type SubmitState = "idle" | "success" | "error";

type PaymentFormValues = {
  taskName: string;
  email: string;
  phoneNumber?: string | null;
  priority: string[];
  dueDate?: string | null;
  description?: string | null;
  attachFileUrl?: string | null;
};

const schema = yup.object({
  taskName: yup.string().required("Task name is required"),
  email: yup.string().email("Invalid email").required("Email is required"),
  phoneNumber: yup.string().optional().nullable(),
  priority: yup.array(yup.string().required()).min(1).required(),
  dueDate: yup.string().optional().nullable(),
  description: yup.string().optional().nullable(),
  attachFileUrl: yup.string().optional().nullable(),
});

export function PaymentRequestForm({ onSuccess }: PaymentRequestFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [submitState, setSubmitState] = useState<SubmitState>("idle");
  const t = useTranslations("settings.payment_request");
  const methods = useForm<PaymentFormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      taskName: "",
      email: "",
      phoneNumber: "",
      priority: ["Medium"],
      dueDate: "",
      description: "",
      attachFileUrl: "",
    },
    mode: "onSubmit",
  });

  const onSubmit = methods.handleSubmit(async (values) => {
    setIsSubmitting(true);
    setApiError(null);
    setSubmitState("idle");

    try {
      const payload: CreatePaymentPayload = {
        taskName: values.taskName,
        email: values.email,
        phoneNumber: values.phoneNumber ? values.phoneNumber : undefined,
        priority: values.priority?.[0],
        dueDate: values.dueDate ? values.dueDate : undefined,
        description: values.description ? values.description : undefined,
        attachFileUrl: values.attachFileUrl ? values.attachFileUrl : undefined,
      };

      const res = await fetch("/api/notion/post-request-form/payment", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = (await res.json()) as { ok?: boolean; error?: string };
      if (!res.ok || !data.ok) {
        throw new Error(data.error || "Failed to submit request");
      }

      setSubmitState("success");
    } catch (e) {
      setApiError(e instanceof Error ? e.message : String(e));
      setSubmitState("error");
    } finally {
      setIsSubmitting(false);
    }
  });

  if (submitState === "success") {
    return (
      <VStack align="stretch" gap={4} pt={2}>
        <Text fontWeight="semibold">{t("sussess_message")}</Text>
        <Button
          onClick={() => onSuccess?.()}
          loading={isSubmitting}
          alignSelf="flex-end"
        >
          Close
        </Button>
      </VStack>
    );
  }

  if (submitState === "error") {
    return (
      <VStack align="stretch" gap={4} pt={2}>
        <Text fontWeight="semibold" color="red.500">
          Submit failed
        </Text>
        {apiError ? <Text color="red.500">{apiError}</Text> : null}
        <HStack justify="flex-end">
          <Button
            onClick={() => {
              setSubmitState("idle");
              setApiError(null);
            }}
            variant="outline"
          >
            Try again
          </Button>
          <Button
            onClick={() => onSuccess?.()}
            loading={isSubmitting}
            alignSelf="flex-end"
          >
            Close
          </Button>
        </HStack>
      </VStack>
    );
  }

  return (
    <FormProvider {...methods}>
      <VStack as="form" align="stretch" gap={4} onSubmit={onSubmit}>
        <InputField
          name="taskName"
          label={t("task_name")}
          isFloating={true}
          isRequired={true}
        />

        <InputField
          name="email"
          label={t("email")}
          isFloating={true}
          isRequired={true}
          type="email"
        />

        <InputField
          name="phoneNumber"
          label={t("phone_number")}
          isFloating={true}
        />

        <SimpleSelectField
          name="priority"
          label={t("priority")}
          listItems={[
            { label: "High", value: "High" },
            { label: "Medium", value: "Medium" },
            { label: "Low", value: "Low" },
          ]}
          isFloating={true}
          isRequired={true}
        />

        <InputField
          name="dueDate"
          label={t("due_date")}
          isFloating={true}
          type="date"
        />

        <TextareaField
          name="description"
          label={t("description")}
          isFloating={true}
          rows={6}
        />

        <InputField
          name="attachFileUrl"
          label={t("attach_file_url")}
          isFloating={true}
        />

        {apiError && <Text color="red.500">{apiError}</Text>}

        <Button size="lg" type="submit" loading={isSubmitting}>
          <HStack gap={2}>
            {t("submit")}
            <Icon as={IoSend} boxSize={4} />
          </HStack>
        </Button>
      </VStack>
    </FormProvider>
  );
}
